import React, { memo, useCallback, useEffect, useState } from "react";
import { OfficeUserStepProps } from "./types";
import { UserRow } from "services/api/users/types";
import { getOfficeUsersAPI } from "services/api/offices/api";
import { Button } from "ui/components/Button/Button";
import UserEditCard from "ui/modules/users/pages/UserDetailPage/components/UserDetailCard/components/UserEditCard/UserEditCard";

const OfficeUserStep: React.FC<OfficeUserStepProps> = ({
  formValues: parentFormValues,
  tenantId,
}) => {
  const officeId = parentFormValues.id;

  const [users, setUsers] = useState<UserRow[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!officeId) return;
    try {
      const result = await getOfficeUsersAPI(officeId);
      setUsers(result);
    } catch {
      setUsers([]);
    }
  }, [officeId]);

  useEffect(() => {
    if (officeId) fetchUsers();
  }, [officeId, fetchUsers]);

  const handleAddUser = useCallback(() => {
    setShowUserModal(true);
  }, []);

  const handleUserSuccess = useCallback(() => {
    setShowUserModal(false);
    fetchUsers();
  }, [fetchUsers]);

  const handleUserClose = useCallback(() => {
    setShowUserModal(false);
  }, []);

  return (
    <div className="flex items-center justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
          Korisnici
        </h1>
        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto ">
          Dodajte korisnike koji pripadaju ovoj poslovnici. Nakon unosa možete
          nastaviti na sljedeći korak.
        </p>

        <div className="flex justify-end mb-4">
          <Button
            variant="primary"
            children="Dodaj korisnika"
            startIcon="/media/icons/plus.svg"
            onClick={handleAddUser}
          />
        </div>

        {users.length > 0 ? (
          <div>
            {users.map((user: UserRow) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded mb-2 last:mb-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                    {user.firstName?.charAt(0)}
                    {user.lastName?.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="flex gap-2 text-xs text-slate-500">
                      {user.email && <span>{user.email}</span>}
                      {user.roles?.length > 0 && (
                        <span>· {user.roles.map((r) => r.name).join(", ")}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-400">
            Nema dodanih korisnika
          </div>
        )}
      </div>

      <UserEditCard
        userId={null}
        tenantId={tenantId}
        officeId={officeId}
        show={showUserModal}
        onSuccess={handleUserSuccess}
        onClose={handleUserClose}
      />
    </div>
  );
};

export default memo(OfficeUserStep);
